import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { SecureDocViewerDialogComponent, SecureDocViewerData, SecureDocMediaType } from '../components/secure-doc-viewer-dialog/secure-doc-viewer-dialog.component';
import { ChatService } from './chat-service';
import { PlatformRegionService } from './shared/platform-region.service';

@Injectable({
  providedIn: 'root'
})
export class ReferenceDocumentService {

  apimUrl: string = '';

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private chatService: ChatService,
    private platformRegionService: PlatformRegionService
  ) { }

  setApimUrl(url: string) {
    this.apimUrl = url;
  }

  openReferenceDocument(ref: any, event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (!ref.FileName) {
      this.toastr.error('Document not available.', '', { positionClass: 'toast-top-right' });
      return;
    }

    const JWT_token = localStorage.getItem('bearer_token');
    const buCode = this.platformRegionService.selected_platform_code;
    const pageNo = ref.page_number || 1;

    const isVideo = this.isVideoFile(ref.FileName);
    const isImage = this.isImageFile(ref.FileName);
    const isAudio = this.isAudioFile(ref.FileName);
    const pdfFileName = (isVideo || isImage || isAudio) ? ref.FileName : this.replaceExtensionWithPdf(ref.FileName);
    const docName = encodeURIComponent(pdfFileName);
    const mimeType = this.getDocumentMimeType(pdfFileName);

    this.chatService.getBusinessUnitByCode(buCode).subscribe(
      (resp: any) => {
        const location = ref.Location || '';
        const locationUrl = (location?.trim() !== '') ? `${this.encodeFolderStructure(location)}/${docName}` : `${docName}`;

        const docUrl = `${this.apimUrl}/${resp.blob_storage_name}/getblobmetadata?business_unit_code=${sessionStorage.getItem('businessUnitCode')}&wwid=${sessionStorage.getItem('userId')}&blobContainer=medtech&fileNameWithExtension=${resp.blob_folder_name}/${locationUrl}&Authorization=${JWT_token}`;

        if (isVideo) {
          this.openVideoInViewer(docUrl, ref.FileName);
        } else if (isAudio) {
          this.openAudioInViewer(docUrl, ref.FileName);
        } else {
          this.chatService.getPdfReport(docUrl, mimeType).subscribe(
            (blob: Blob) => {
              const fileURL = URL.createObjectURL(blob);
              this.openSecureDocViewer(fileURL, pageNo, ref.FileName, isImage ? 'image' : 'pdf');
            },
            (err) => {
              this.toastr.error('Error loading document. Please try again.', '', { positionClass: 'toast-top-right' });
            }
          );
        }
      },
      (err) => {
        this.toastr.error('Error loading document. Please try again.', '', { positionClass: 'toast-top-right' });
      }
    );
  }

  private async openVideoInViewer(encodedUrl: string, title?: string, startStr?: string | number, endStr?: string | number): Promise<void> {
    let mediaUrl: string;
    try {
      mediaUrl = await this.resolvePlayableUrl(encodedUrl);
    } catch {
      this.toastr.error('Error loading video. Please try again.', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.dialog.open(SecureDocViewerDialogComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'secure-doc-viewer-panel',
      autoFocus: false,
      disableClose: false,
      data: {
        docUrl: mediaUrl,
        title: title || 'Video Viewer',
        mediaType: 'video',
        startTime: this.parseTimeToSeconds(startStr),
        endTime: this.parseTimeToSeconds(endStr)
      } as SecureDocViewerData
    });
  }

  private async openAudioInViewer(encodedUrl: string, title?: string, startStr?: string | number, endStr?: string | number): Promise<void> {
    let mediaUrl: string;
    try {
      mediaUrl = await this.resolvePlayableUrl(encodedUrl);
    } catch {
      this.toastr.error('Error loading audio. Please try again.', '', { positionClass: 'toast-top-right' });
      return;
    }

    this.dialog.open(SecureDocViewerDialogComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'secure-doc-viewer-panel',
      autoFocus: false,
      disableClose: false,
      data: {
        docUrl: mediaUrl,
        title: title || 'Audio Viewer',
        mediaType: 'audio',
        startTime: this.parseTimeToSeconds(startStr),
        endTime: this.parseTimeToSeconds(endStr)
      } as SecureDocViewerData
    });
  }

  openSecureDocViewer(docUrl: string, pageNumber?: number, title?: string, mediaType: SecureDocMediaType = 'pdf'): void {
    this.dialog.open(SecureDocViewerDialogComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'secure-doc-viewer-panel',
      autoFocus: false,
      disableClose: false,
      data: {
        docUrl: docUrl,
        pageNumber: pageNumber || 1,
        title: title || 'Document Viewer',
        mediaType: mediaType
      } as SecureDocViewerData
    });
  }

  encodeFolderStructure(url: string): string {
    if (url === null) {
      return '';
    }
    const folders = url.split(/[\/|]/);
    const encodedFolders = folders.map(folder => encodeURIComponent(folder));
    return encodedFolders.join('/');
  }

  private replaceExtensionWithPdf(fileName: string): string {
    const extensions = ['.pptx', '.ppt', '.docx', '.doc'];
    const matched = extensions.find(ext => fileName.toLowerCase().endsWith(ext));
    return matched ? fileName.replace(new RegExp(matched.replace('.', '\\.') + '$', 'i'), '.pdf') : fileName;
  }

  private getDocumentMimeType(fileName: string): string {
    const ext = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
      mp4: 'video/mp4', mkv: 'video/x-matroska', mov: 'video/quicktime', webm: 'video/webm',
      mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4'
    };
    return mimeTypes[ext] || 'application/pdf';
  }

  private isVideoFile(fileName: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
    return ['mp4', 'mp2', 'mkv', 'mov', 'webm'].includes(ext);
  }

  private isImageFile(fileName: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
    return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  }

  private isAudioFile(fileName: string): boolean {
    if (!fileName) return false;
    const ext = fileName.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
    return ['mp3', 'wav', 'm4a', 'aac', 'ogg'].includes(ext);
  }

  private parseTimeToSeconds(t?: string | number): number | undefined {
    if (t == null) return undefined;
    const s = String(t).trim();
    if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
    const parts = s.split(':').map(p => Number(p));
    if (parts.some(Number.isNaN)) return undefined;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return undefined;
  }

  private async resolvePlayableUrl(encodedUrl: string): Promise<string> {
    const decoded = (() => { try { return decodeURIComponent(encodedUrl); } catch { return encodedUrl; } })();
    if (/getblobmetadata/i.test(decoded)) {
      try {
        const resp = await fetch(decoded, { credentials: 'include' });
        if (!resp.ok) return decoded;
        const json = await resp.json();
        return (json?.file_url || json?.signedUrl || json?.blobUrl || json?.url || json?.mediaUrl) ?? decoded;
      } catch {
        return decoded;
      }
    }
    return decoded;
  }

}
